import { useMemo } from "react";
import type { Person } from "@b2bportal/air-booking-api";
import { PassengerType } from "@b2bportal/core-types";
import { getPassengerType } from "@b2bportal/core-utilities";
import type { Traveler } from "@b2bportal/traveler-api";

interface UsePassengerGroupingParams<T = Person> {
  passengerList: Array<T>;
  tripDate?: string | Date;
}

export type PassengersByAge<T> = {
  [PassengerType.Adult]: Array<T>;
  [PassengerType.Child]: Array<T>;
  [PassengerType.InfantInSeat]: Array<T>;
};

export interface UsePassengerGroupingReturn<T> {
  uniquePassengers: Array<T>;
  passengersByAge: PassengersByAge<T>;
}

export const isTraveler = (input: unknown): input is Traveler =>
  typeof input === "object" && (input as Traveler).name != null;

export const usePassengerGrouping = <T = Person>({
  passengerList,
  tripDate,
}: UsePassengerGroupingParams<T>): UsePassengerGroupingReturn<T> => {
  // Generate a list of unique passengers
  const uniquePassengers = useMemo(() => {
    const uniqueIndexByPassenger: Record<string, number> = {};

    passengerList.forEach((passenger, index) => {
      const { givenName, surname, dateOfBirth } = isTraveler(passenger)
        ? {
            givenName: passenger.name?.first,
            surname: passenger.name?.last,
            dateOfBirth: passenger.dateOfBirth,
          }
        : (passenger as Person);
      const uniqueKey = `${givenName}${surname}${dateOfBirth}`;

      if (!uniqueIndexByPassenger[uniqueKey]) {
        uniqueIndexByPassenger[uniqueKey] = index;
      }
    });

    // Generate array of passengers from unique index
    return Object.values(uniqueIndexByPassenger).map(
      (index) => passengerList[index]
    );
  }, [passengerList]);

  // Passengers by age categories
  const passengersByAge = useMemo(() => {
    const adults: Array<T> = [];
    const children: Array<T> = [];
    const infants: Array<T> = [];

    uniquePassengers.forEach((passenger) => {
      const passengerType = getPassengerType({
        dateOfBirth: (passenger as Person | Traveler).dateOfBirth,
        tripDate,
      });
      switch (passengerType) {
        case PassengerType.Adult:
          adults.push(passenger);
          break;
        case PassengerType.Child:
          children.push(passenger);
          break;
        default:
          infants.push(passenger);
          break;
      }
    });

    return {
      [PassengerType.Adult]: adults,
      [PassengerType.Child]: children,
      [PassengerType.InfantInSeat]: infants,
    };
  }, [uniquePassengers, tripDate]);

  return {
    uniquePassengers,
    passengersByAge,
  };
};
